'use client';

import { IconButton } from '@7f-ui/components/button/icon-button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@7f-ui/components/tooltip/tooltip';
import { trackPaymentInfo } from '@feat/feat-tracking/cta';
import { InfoIcon } from '@phosphor-icons/react';
import { ApplePayIcon } from '@shared/composites/payment-icons/apple-pay-icon';
import { GooglePayIcon } from '@shared/composites/payment-icons/google-pay-icon';
import { PayPalIcon } from '@shared/composites/payment-icons/paypal-icon';
import type { PaymentPriorityExperiment } from '@shared/hooks/use-payment-priority-ab';
import { shopTestId } from '@utils/utils-shop-test-ids';
import { type RefObject, useRef, useState } from 'react';
import { usePreferredWallet, type WalletType } from '../_hooks/use-preferred-wallet';
const WALLETS: Record<WalletType, {
  Icon: typeof ApplePayIcon;
  label: string;
  other: string;
}> = {
  applepay: {
    Icon: ApplePayIcon,
    label: 'Apple Pay',
    other: 'Google Pay'
  },
  googlepay: {
    Icon: GooglePayIcon,
    label: 'Google Pay',
    other: 'Apple Pay'
  }
};
type Props = Pick<PaymentPriorityExperiment, 'experimentId' | 'variationId'> & {
  hasTrackedInteractionRef: RefObject<boolean>;
};
export function AppPaymentPriorityHint({
  experimentId,
  variationId,
  hasTrackedInteractionRef
}: Props) {
  const wallet = usePreferredWallet();
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const isPressOpenRef = useRef(false);
  const {
    Icon: WalletIcon,
    label: walletLabel,
    other: otherWallet
  } = WALLETS[wallet];
  const trackOpen = (interaction: 'hover' | 'press') => {
    if (hasTrackedInteractionRef.current) return;
    hasTrackedInteractionRef.current = true;
    trackPaymentInfo(interaction === 'press' ? 'click' : 'hover', {
      walletType: wallet,
      experimentId,
      variationId
    });
  };
  return <div className="flex items-center gap-2" data-testid={shopTestId('PaymentPriorityHint.root')}>
      <ul className="flex items-center gap-2" aria-label={`${walletLabel}, PayPal`}>
        <li data-testid={shopTestId('PaymentPriorityHint.wallet')} data-wallet={wallet}>
          <WalletIcon bgColor="#F1F0EB" />
        </li>
        <li>
          <PayPalIcon bgColor="#F1F0EB" />
        </li>
      </ul>
      <span className="font-base-copy font-semibold">Sicher und einfach.</span>
      <TooltipProvider>
        <Tooltip open={isTooltipOpen} onOpenChange={(isOpen, eventDetails) => {
        // A touch tap can emit a synthetic mouseleave on hybrid devices.
        // Keep press-opened hints open until another press, dismissal, or blur.
        if (eventDetails.reason === 'trigger-hover' && isPressOpenRef.current) return;
        if (!isOpen) isPressOpenRef.current = false;
        setIsTooltipOpen(isOpen);
        if (isOpen && eventDetails.reason === 'trigger-hover') trackOpen('hover');
      }}>
          <TooltipTrigger closeOnClick={false} render={<IconButton Icon={InfoIcon} iconWeight="bold" size="icon" variant="transparent" iconOnly className="rounded-full text-secondary-text hover:text-primary focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-2" iconClassName="size-5" aria-label="Mehr zu den Zahlarten" data-testid={shopTestId('PaymentPriorityHint.info')} onClick={() => {
          const isOpen_0 = !isTooltipOpen;
          isPressOpenRef.current = isOpen_0;
          if (isOpen_0) trackOpen('press');
          setIsTooltipOpen(isOpen_0);
        }} />} />
          <TooltipContent variant="card" data-testid={shopTestId('PaymentPriorityHint.tooltip')}>
            <span className="font-base-copy font-semibold">Mehr zu den Zahlarten</span>
            <span className="font-base-copy">
              {`Auch ${otherWallet}, Visa, Mastercard, American Express oder Klarna möglich - deine Zahlart wählst du am Ende.`}
            </span>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>;
}